import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  of,
  Subject,
  Subscription as RxSubscription,
} from 'rxjs';
import { fromFetch } from 'rxjs/fetch';
import { catchError, map, shareReplay, switchMap, takeUntil } from 'rxjs/operators';

import { AbilitiesSelectors } from '@recruitee/abilities-data';
import { Ability } from '@recruitee/abilities-types';
import { AccountSelectors } from '@recruitee/account-data';
import { filterBoolean } from '@recruitee/common';
import { SubscriptionService } from '@recruitee/company-logic';
import type { Company } from '@recruitee/company-types';
import { DevFlag, DevFlagSelectors } from '@recruitee/dev-flags-data';
import { AppStore } from '@recruitee/legacy-app-store';
import {
  isCurrencyLocked,
  isPp24,
  mapSuggestedCurrency,
  PricingFeatureLoadedModel,
  PricingFeatureModel,
  TrialProductCode,
} from '@recruitee/pricing';
import type { Currency } from '@recruitee/pricing-types';
import { CompanyVerificationRequestStatus } from '@recruitee/spam-verification-data';
import type { PricingFeatureSubscription } from '@recruitee/subscription-types';

@Injectable({ providedIn: 'root' })
export class PricingFeatureConnectedModel extends BehaviorSubject<PricingFeatureModel> {
  private geoCountryCode$: Observable<string> = fromFetch(
    'https://api.ipdata.co/country_code?api-key=94c7fbfbf4ef81024167b475c71b479f80b6a6d02c84ff94c738e00a',
  ).pipe(
    switchMap(res => res.text()),
    shareReplay(1),
    catchError(err => {
      console.warn('Location lookup failed.');
      console.warn(err);
      // it will be mapped by guard function mapCountryCodeToCurrency, sometimes adblock prevents ip check on user
      return of('not determined');
    }),
  );

  private disconnect$ = new Subject<void>();
  private connected = false;
  private canManageSubscription: boolean;

  constructor(
    private appStore: AppStore,
    private store: Store,
    private subscriptionService: SubscriptionService,
  ) {
    super({
      loaded: false,
      loading: true,
    });

    this.store
      .select(AbilitiesSelectors.hasOne(Ability.ManageSubscription))
      .subscribe(canManageSubscription => (this.canManageSubscription = canManageSubscription));
  }

  public subscribe(observer: any): RxSubscription {
    if (!this.connected) {
      this.connect();
    }

    const sub = super.subscribe(observer);

    return new RxSubscription(() => {
      if (this.observers.length === 0) {
        this.disconnect$.next();
        this.connected = false;
      }

      sub.unsubscribe();
    });
  }

  private connect() {
    this.store
      .select(AccountSelectors.currentCompany)
      .pipe(
        filterBoolean,
        switchMap((company: Company & { subscription: PricingFeatureSubscription }) => {
          const explicitCountryCode = company.countryCode;
          const billingCurrency = <Currency | undefined>company.billingInformation?.currency;

          const countryCode$ = explicitCountryCode ? of(explicitCountryCode) : this.geoCountryCode$;
          const currency$: Observable<Currency> = billingCurrency
            ? of(billingCurrency)
            : countryCode$.pipe(map(mapSuggestedCurrency));

          const currencyLock$ = countryCode$.pipe(
            map(countryCode => !!billingCurrency || isCurrencyLocked(countryCode)),
          );

          return combineLatest([
            of(company),
            countryCode$,
            currency$,
            currencyLock$,
            this.store.select(DevFlagSelectors.isEnabled(DevFlag.PP24Enabled)),
          ]);
        }),
        takeUntil(this.disconnect$),
      )
      .subscribe(([company, countryCode, currency, currencyLock, pp24EnabledFlag]) =>
        this.next(this.toModel(company, countryCode, currency, currencyLock, pp24EnabledFlag)),
      );

    this.connected = true;
  }

  // for now we keep logic with trial OR subscription
  // keep in mind that in future we will support both subscription & trial at the same time
  // eslint-disable-next-line max-lines-per-function
  private toModel(
    company: Company & { subscription: PricingFeatureSubscription },
    countryCode: string,
    currency: Currency,
    currencyLock: boolean,
    pp24EnabledFlag: boolean,
  ): PricingFeatureLoadedModel {
    const { billingInformation, trialPeriodEnd, subscription, chargebeeEntity } = company;
    const isPP24 = pp24EnabledFlag || isPp24(subscription.tier);

    return {
      loaded: true,
      loading: false,
      countryCode,
      currency,
      currencyLock,
      chargebeeEntity,
      subscriptionStatus: {
        expired: this.subscriptionService.isExpired(),
        blocked: this.subscriptionService.isBlocked(),
        canceled: this.subscriptionService.isCanceled(),
        pastDue: this.subscriptionService.isPastDue(),
        paused: this.subscriptionService.isPaused(),
        future: this.subscriptionService.isFuture(),
        inactive: this.subscriptionService.isInactive(),
        trialing: this.subscriptionService.isTrialing(),
        nonRenewing: this.subscriptionService.isNonRenewing(),
      },
      ...(subscription && !['inactive', 'trialing'].includes(subscription.status)
        ? { subscription }
        : {
            trial: {
              untilDate: trialPeriodEnd,
              productCode: TrialProductCode(isPP24),
              expired: this.subscriptionService.isTrialEnded(),
            },
          }),
      externalBilling: company.externalBilling,
      billingInformation: billingInformation ? billingInformation : {},
      canManageSubscription: this.canManageSubscription,
      jobSlotsUsed: company.usedJobs,
      slotsTotal: subscription.slots,
      hasSampleData: company.hasSampleData,
      changeCurrency: currency => {
        this.next(this.toModel(company, countryCode, currency, currencyLock, pp24EnabledFlag));
      },
      spamVerificationStatus: CompanyVerificationRequestStatus.Pending,
      isPP24,
      isAgency: company.staffingAgency,
    };
  }
}
